import { graphql } from '@/__generated__';
import { AncestorNodeType } from '@/__generated__/graphql';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { useQueryStable } from '../useQueryStable';
import { GetAppliedNode } from './testUtil';

type Result = ReturnType<typeof useModelByEnvironment>[0];

export type ModelResponse = NonNullable<Result['data']>;

export type RequestedModelAppliedNode = GetAppliedNode<'models', ModelResponse>;

// https://github.com/dbt-labs/codex-api/blob/main/schema.graphql
// https://github.com/dbt-labs/codex-api/blob/main/frontend/package.json#L33
// https://github.com/dbt-labs/codex-api/blob/main/frontend/codegen.yml
// generates: https://github.com/dbt-labs/codex-api/blob/main/frontend/src/typings/codex.ts
export const GetModelByUniqueId = graphql(/* GraphQL */ `
  query Model(
    $environmentId: BigInt!
    $userId: BigInt
    $uniqueIds: [String!]
    $types: [AncestorNodeType!]!
  ) {
    environment(id: $environmentId, userId: $userId) {
      adapterType
      modelQueryHistoryState {
        state
      }
      applied {
        lastUpdatedAt
        latestGitSha
        models(first: 1, filter: { uniqueIds: $uniqueIds }) {
          edges {
            node {
              fqn
              access
              alias
              name
              compiledCode
              contractEnforced
              database
              deprecationDate
              description
              isDescriptionInherited
              filePath
              group
              language
              materializedType
              meta
              packageName
              patchPath
              projectId
              rawCode
              releaseVersion
              schema
              tags
              uniqueId
              usageQueryCount
              version
              healthIssues
              parents {
                name
                uniqueId
                resourceType
                __typename
              }
              ancestors(types: $types) {
                uniqueId
                resourceType
                ... on SourceAppliedStateNestedNode {
                  freshness {
                    freshnessStatus
                    freshnessChecked
                  }
                }
              }
              children {
                uniqueId
              }
              versions {
                name
                releaseVersion
                uniqueId
              }
              executionInfo {
                executeCompletedAt
                executionTime
                lastJobDefinitionId
                lastRunId
                lastRunStatus
              }
              tests {
                columnName
                name
                uniqueId
                testType
                executionInfo {
                  lastRunStatus
                  lastRunError
                }
                testMetadata {
                  name
                  kwargs
                }
              }
              constraints {
                columns
                expression
                name
                type
                warnUnenforced
                warnUnsupported
              }
              catalog {
                type
                owner
                comment
                bytesStat
                rowCountStat
                primaryKey
                columns {
                  comment
                  description
                  descriptionOriginColumnName
                  descriptionOriginResourceUniqueId
                  name
                  tags
                  type
                  meta
                }
                stats {
                  description
                  id
                  include
                  label
                  value
                }
              }
            }
          }
        }
      }
    }
  }
`);

export const GetModelListByUniqueIdWithoutQueryHistory = graphql(/* GraphQL */ `
  query ModelWithoutQueryHistory(
    $environmentId: BigInt!
    $userId: BigInt
    $uniqueIds: [String!]
    $types: [AncestorNodeType!]!
  ) {
    environment(id: $environmentId, userId: $userId) {
      adapterType
      modelQueryHistoryState {
        state
      }
      applied {
        lastUpdatedAt
        latestGitSha
        models(first: 1, filter: { uniqueIds: $uniqueIds }) {
          edges {
            node {
              fqn
              access
              alias
              name
              compiledCode
              contractEnforced
              database
              deprecationDate
              description
              isDescriptionInherited
              filePath
              group
              language
              materializedType
              meta
              packageName
              patchPath
              projectId
              rawCode
              releaseVersion
              schema
              tags
              uniqueId
              version
              healthIssues
              parents {
                name
                uniqueId
                resourceType
                __typename
              }
              ancestors(types: $types) {
                uniqueId
                resourceType
                ... on SourceAppliedStateNestedNode {
                  freshness {
                    freshnessStatus
                    freshnessChecked
                  }
                }
              }
              children {
                uniqueId
              }
              versions {
                name
                releaseVersion
                uniqueId
              }
              executionInfo {
                executeCompletedAt
                executionTime
                lastJobDefinitionId
                lastRunId
                lastRunStatus
              }
              tests {
                columnName
                name
                uniqueId
                testType
                executionInfo {
                  lastRunStatus
                  lastRunError
                }
                testMetadata {
                  name
                  kwargs
                }
              }
              constraints {
                columns
                expression
                name
                type
                warnUnenforced
                warnUnsupported
              }
              catalog {
                type
                owner
                comment
                bytesStat
                rowCountStat
                primaryKey
                columns {
                  comment
                  description
                  descriptionOriginColumnName
                  descriptionOriginResourceUniqueId
                  name
                  tags
                  type
                  meta
                }
                stats {
                  description
                  id
                  include
                  label
                  value
                }
              }
            }
          }
        }
      }
    }
  }
`);

type ModelByEnvironmentParams = {
  environmentId: number | undefined;
  userId?: number | 'loading' | null;
  uniqueId: string;
  pause?: boolean;
};

export const useModelByEnvironment = ({
  environmentId = 0,
  userId,
  uniqueId,
  pause,
}: ModelByEnvironmentParams) => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const queryChoice = hasAccountFeature('explorerQueryHistory')
    ? GetModelByUniqueId
    : GetModelListByUniqueIdWithoutQueryHistory;

  const query = useQueryStable({
    query: queryChoice,
    variables: {
      environmentId,
      userId: userId as number | null,
      uniqueIds: [uniqueId],
      types: [AncestorNodeType.Source],
    },
    pause: pause || !environmentId || !uniqueId || userId === 'loading',
  });

  useCacheMonitor({
    applied: query[0].data?.environment.applied?.lastUpdatedAt,
  });

  return query;
};
